import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Stack, FormControlLabel, Switch } from "@mui/material";
import {
  Dialog,
  HookTextField,
  Text,
  COLORS,
} from "@asayinc/component-library";
import { toast } from "react-toastify";

import { User, CreateEditUserPayload } from "src/store/user/types";
import {
  useCreateUserMutation,
  useUpdateUserMutation,
} from "src/store/user/api";
import { useCurrentUserQuery } from "src/store/user";

interface CreateUpdateUserDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
}

interface CreateEditUserForm {
  firstName: string;
  lastName: string;
  email: string;
  isIntermediaryPortalAdmin: boolean;
}

export function CreateUpdateUserDialog({
  open,
  onClose,
  user,
}: CreateUpdateUserDialogProps) {
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const { data: currentUser } = useCurrentUserQuery();

  const isEditing = !!user;
  const isLoading = isCreating || isUpdating;

  const form = useForm<CreateEditUserForm>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      isIntermediaryPortalAdmin: false,
    },
  });

  const { handleSubmit, reset, watch, setValue } = form;
  const isAdmin = watch("isIntermediaryPortalAdmin");

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        isIntermediaryPortalAdmin: user.isIntermediaryPortalAdmin || false,
      });
    } else {
      reset({
        firstName: "",
        lastName: "",
        email: "",
        isIntermediaryPortalAdmin: false,
      });
    }
  }, [user, reset]);

  const onSubmit = (data: CreateEditUserForm) => {
    const payload: CreateEditUserPayload = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      isIntermediaryPortalAdmin: data.isIntermediaryPortalAdmin,
    };

    if (isEditing && user) {
      updateUser({ id: user.id, payload })
        .unwrap()
        .then(() => {
          toast.success("User updated successfully");
          onClose();
        })
        .catch((e) => {
          toast.error(`${e?.status} - ${JSON.stringify(e?.data)}`);
        });
    } else {
      createUser(payload)
        .unwrap()
        .then(() => {
          toast.success("User created successfully");
          onClose();
        })
        .catch((e) => {
          toast.error(`${e?.status} - ${JSON.stringify(e?.data)}`);
        });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEditing ? "Edit User" : "Create User"}
      content={
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={4}>
              {!isEditing && currentUser && (
                <Text
                  variant="body3"
                  sx={{ color: COLORS.P_MED_LOW_EMPHASIS, pb: 2 }}
                >
                  This user will be assigned to the same companies as you:
                  {currentUser.solicitors?.length > 0 && (
                    <Text component="span" display="block" sx={{ mt: 1 }}>
                      • Solicitors:{" "}
                      {currentUser.solicitors.map((s) => s.name).join(", ")}
                    </Text>
                  )}
                  {currentUser.tabulators?.length > 0 && (
                    <Text component="span" display="block" sx={{ mt: 0.5 }}>
                      • Tabulators:{" "}
                      {currentUser.tabulators.map((t) => t.name).join(", ")}
                    </Text>
                  )}
                  {currentUser.claimsAdministrators?.length > 0 && (
                    <Text component="span" display="block" sx={{ mt: 0.5 }}>
                      • Claims Administrators:{" "}
                      {currentUser.claimsAdministrators
                        .map((c) => c.name)
                        .join(", ")}
                    </Text>
                  )}
                </Text>
              )}
              <HookTextField
                name="firstName"
                label="First Name"
                rules={{ required: "First name is required" }}
              />
              <HookTextField
                name="lastName"
                label="Last Name"
                rules={{ required: "Last name is required" }}
              />
              <HookTextField
                name="email"
                label="Email"
                disabled={isEditing}
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isAdmin}
                    onChange={(e) =>
                      setValue("isIntermediaryPortalAdmin", e.target.checked)
                    }
                  />
                }
                label="Has admin privileges"
              />
            </Stack>
          </form>
        </FormProvider>
      }
      buttonActions={[
        {
          label: "Cancel",
          onClick: onClose,
          variant: "secondary",
          size: "small",
          disabled: isLoading,
        },
        {
          label: isLoading ? "Saving..." : isEditing ? "Update" : "Create",
          onClick: handleSubmit(onSubmit),
          loading: isLoading,
          size: "small",
        },
      ]}
      ctaOrientation="horizontal"
    />
  );
}
